/**
 * Live hero — in-progress state for the football-team adaptive hero.
 *
 * Polls the live match endpoint via useLiveMatchData. Server passes the
 * initial snapshot; the hook takes over updates every 30s.
 */

"use client";

import Link from "next/link";
import type { FUSportsMatch } from "fansunited-data-layer";
import { useLiveMatchData } from "@/features/live-data/useLiveMatchData";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";

interface LiveHeroProps {
    match: FUSportsMatch;
    matchHref: string;
    liveLabel: string;
    matchCentreLabel: string;
}

export function LiveHero({ match, matchHref, liveLabel, matchCentreLabel }: LiveHeroProps) {
    // The polling helper falls back to match.id when fansUnitedMatchId is null.
    const matchWithNativeId: FUSportsMatchWithNativeId = { ...match, fansUnitedMatchId: null };
    const { score, matchMinute } = useLiveMatchData({ match: matchWithNativeId, enablePolling: true });

    const home = match.competitorOne;
    const away = match.competitorTwo;
    const homeScore = score.competitorOne ?? match.score?.competitorOne ?? "0";
    const awayScore = score.competitorTwo ?? match.score?.competitorTwo ?? "0";

    return (
        <section
            data-hero-state="live"
            className="relative w-full h-[420px] md:h-[520px] rounded-lg overflow-hidden bg-gradient-to-br from-red-700 to-red-900 text-white flex flex-col justify-between p-6 md:p-10"
        >
            <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-white text-red-700 text-xs uppercase tracking-widest font-bold">
                    <span className="w-2 h-2 rounded-full bg-red-700 animate-pulse" />
                    {liveLabel}
                </span>
                {matchMinute && (
                    <span className="text-lg md:text-2xl font-extrabold tabular-nums">{matchMinute}'</span>
                )}
                {match.competition?.name && (
                    <span className="text-xs uppercase tracking-widest opacity-80">{match.competition.name}</span>
                )}
            </div>

            <div className="flex items-center justify-around">
                <div className="flex flex-col items-center gap-3 flex-1">
                    {home.assets?.logo && <img src={home.assets.logo} alt={home.name} className="h-16 md:h-24 w-auto" />}
                    <div className="text-base md:text-xl font-semibold text-center">{home.name}</div>
                </div>
                <div className="text-5xl md:text-7xl font-extrabold tabular-nums px-6">
                    {homeScore} <span className="opacity-40">-</span> {awayScore}
                </div>
                <div className="flex flex-col items-center gap-3 flex-1">
                    {away.assets?.logo && <img src={away.assets.logo} alt={away.name} className="h-16 md:h-24 w-auto" />}
                    <div className="text-base md:text-xl font-semibold text-center">{away.name}</div>
                </div>
            </div>

            <div className="flex justify-center">
                <Link
                    href={matchHref}
                    prefetch={false}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-white/15 hover:bg-white/25 text-sm font-semibold uppercase tracking-wide backdrop-blur-sm"
                >
                    {matchCentreLabel} →
                </Link>
            </div>
        </section>
    );
}
